import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useFormat } from '@paige/format';
import { itemStateOf } from './flow-item-words';
import type { FlowItem, FlowItemsPage, FlowItemsQuery, FlowPlace } from './flow-types';

/** How long a pointer rests on a stage before its items are asked for: a pass across the picture asks nothing. */
export const PEEK_DELAY_MS = 350;
/** A stage's three oldest are kept this long and shared by every look at it in that time. */
export const PEEK_KEPT_MS = 15_000;

interface Kept {
  at: number;
  items: FlowItem[];
  total: number;
}

export interface PeekState {
  place: FlowPlace | null;
  items: FlowItem[] | null;
  total: number;
  failed: boolean;
}

const NONE: PeekState = { place: null, items: null, total: 0, failed: false };

type Load = ((q: FlowItemsQuery) => Promise<FlowItemsPage>) | undefined;

/** Reads a place's three oldest, or answers from what was read in the last quarter minute. */
function usePeekRead(
  loadItems: Load,
  company: string | null,
  setState: (s: (p: PeekState) => PeekState) => void,
) {
  const kept = useRef(new Map<string, Kept>());
  return useCallback(
    (place: FlowPlace) => {
      const key = `${company ?? '*'}|${place}`;
      const hit = kept.current.get(key);
      if (hit && Date.now() - hit.at < PEEK_KEPT_MS) {
        setState(() => ({ place, items: hit.items, total: hit.total, failed: false }));
        return;
      }
      setState(() => ({ place, items: null, total: 0, failed: false }));
      loadItems?.({ place, company, sort: 'oldest', offset: 0, limit: 3 }).then(
        (page) => {
          kept.current.set(key, { at: Date.now(), items: page.items, total: page.total });
          setState((s) =>
            s.place === place ? { place, items: page.items, total: page.total, failed: false } : s,
          );
        },
        () => setState((s) => (s.place === place ? { ...s, failed: true } : s)),
      );
    },
    [loadItems, company, setState],
  );
}

/**
 * Hovering (or focusing) a stage previews its three oldest items (`GET /admin/flow/items?limit=3`): asked only after
 * the pointer rests there, never while the picture is in the past or paused, and kept a quarter minute per stage.
 */
export function useFlowPeek(loadItems: Load, company: string | null, live: boolean) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [state, setState] = useState<PeekState>(NONE);
  const read = usePeekRead(loadItems, company, setState);
  const enter = useCallback(
    (place: FlowPlace) => {
      if (!loadItems || !live) return;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => read(place), PEEK_DELAY_MS);
    },
    [loadItems, live, read],
  );
  const leave = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    setState(NONE);
  }, []);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);
  return { peek: state, enter, leave };
}

/** The preview on a stage: its three oldest, whose and how long, and how many more. */
export function FlowPeek({ peek, here }: { peek: PeekState; here: number }): ReactNode {
  const f = useFormat();
  if (here === 0) return null;
  const body = peek.failed ? (
    <span className="fl-peek__note">Could not read them; click to open</span>
  ) : !peek.items ? (
    <span className="fl-peek__note">Reading the oldest</span>
  ) : (
    <>
      {peek.items.map((item) => {
        const state = itemStateOf(item);
        return (
          <span key={item.id} className={`fl-peek__item fl-peek__item--${state.tone}`}>
            <span className="fl-peek__who">
              {item.companyName}
              {item.jobName ? ` · ${item.jobName}` : ''}
            </span>
            <span className="fl-peek__what">
              {f.duration(item.ageSeconds)} · {state.words}
            </span>
          </span>
        );
      })}
      {peek.total > peek.items.length ? (
        <span className="fl-peek__note">
          and {f.int(peek.total - peek.items.length)} more; click to open
        </span>
      ) : null}
    </>
  );
  return (
    <span className="fl-peek" role="tooltip">
      <span className="fl-peek__h">Oldest here</span>
      {body}
    </span>
  );
}
